import { useMutation, useQueryClient } from "@tanstack/react-query";
import { HistoryOutline24Icon } from "@vygruppen/spor-icon-react";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { PanelAction } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/ActionsOverflowMenu";
import { isStopCancelled } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/utils";
import { CancellationProps } from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/Itinerary/VehicleCancellation/VehicleCancellation";
import { FormEvent, useState } from "react";
import { ActionModal } from "shared/components/ActionModal";
import { Text } from "shared/components/typography/TitleStyles";
import { AlternativeTransportCancelReq } from "shared/types/alternativeTransport";

export const useVehicleUndoCancellationAction = (
  vehicle: CancellationProps["vehicle"],
): PanelAction | null => {
  const cancelledStops = vehicle.itinerary?.stops.filter((stop) =>
    isStopCancelled(stop, vehicle.vehicleStatus),
  );

  const [showModal, setShowModal] = useState(false);

  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (body: AlternativeTransportCancelReq) =>
      axiosClient.patch<any>(
        `${getBackendUrl()}/alternativeTransports/${vehicle.id}/cancel`,
        body,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["altTransDetails"] });
    },
  });

  if (!cancelledStops || cancelledStops.length <= 0) {
    return null;
  }

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    mutation.mutate({
      cancelledStops: cancelledStops.map((stop) => ({
        stopReference: stop.stopReference,
        stopName: stop.name,
        isArrivalCancelled: false,
        isDepartureCancelled: false,
        stopActivity: stop.activity,
      })),
    });
  };

  const onClose = () => {
    setShowModal(false);
    mutation.reset();
  };

  return {
    key: "undo-cancel",
    label: "Angre kansellering",
    leftIcon: <HistoryOutline24Icon />,
    buttonVariant: "ghost",
    buttonSize: "sm",
    onSelect: () => setShowModal(true),
    modal: showModal ? (
      <ActionModal
        title={`Angre kansellert avgang ${vehicle.workShiftPlan?.shiftId}`}
        actionTitle="Angre kansellering"
        onClose={onClose}
        onSubmit={onSubmit}
        loading={mutation.isPending}
        isSuccess={mutation.isSuccess}
        isError={mutation.isError}
        successMessage="Angring vellykket."
        failureMessage="Kunne ikke angre kansellert avgang. Prøv igjen, eller kontakt IT hvis feilen vedvarer."
      >
        <Text>
          Er du sikker på at du vil angre alle kanselleringer på avgangen?
        </Text>
      </ActionModal>
    ) : null,
  };
};
